import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { LinkIcon } from "lucide-react";
import { sessionApi } from "../../api/account";
import { AuthBadge, AuthLayout } from "../../components/account/AuthLayout";
import { usePageTitle } from "../catalogue/usePageTitle";
import { errorMessage, useSetSession } from "./session";

/**
 * Where an emailed sign-in link lands. The page posts the token back rather than the link
 * signing in on GET, so an email scanner that follows links can't use it up.
 */
export function SignInLinkRoute() {
  usePageTitle("Signing you in");
  const navigate = useNavigate();
  const setSession = useSetSession();
  const token = useSearchParams()[0].get("token") ?? "";
  const redeem = useMutation({
    mutationFn: () => sessionApi.redeemLink(token),
    onSuccess: (session) => {
      setSession(session);
      navigate("/", { replace: true });
    },
  });

  // Once only: the link works once, and development renders effects twice.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    redeem.mutate();
  }, [redeem, token]);

  return (
    <AuthLayout>
      {redeem.isError || !token ? (
        <>
          <AuthBadge icon={LinkIcon} tone="danger" />
          <h1 className="mb-4 type-h1 [font-size:var(--text-2xl)] tracking-tight">This link didn't work</h1>
          <p className="mb-7 type-body text-text-body">
            {(token ? errorMessage(redeem.error) ?? "" : "This link is incomplete").replace(/\.$/, "")}. Each link works once and expires after 15
            minutes.
          </p>
          <Link to="/sign-in" className="inline-flex min-h-11 items-center border-b-0 type-body font-semibold">
            Sign in or send a new link
          </Link>
        </>
      ) : (
        <p role="status" className="type-body text-text-muted">
          Signing you in…
        </p>
      )}
    </AuthLayout>
  );
}
